"use client";
import React, { useState, useEffect } from "react";
import { useParams } from "next/navigation";
import Link from "next/link";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

async function getPost(slug: string) {
  const res = await fetch(`/api/blog/${slug}`);
  if (!res.ok) return null;
  return res.json();
}

export default function BlogPostPage() {
  const params = useParams();
  const slug = params?.slug as string;
  const [post, setPost] = useState<any>(null);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  useEffect(() => {
    if (!slug) return;
    getPost(slug).then(data => {
      setPost(data);
      setLikeCount(data?.likes || 0);
      setLiked(typeof window !== 'undefined' && localStorage.getItem(`blog-liked-${slug}`) === '1');
    });
  }, [slug]);

  async function handleLike() {
    if (liked || !post) return;
    const res = await fetch(`/api/blog/${post.id}`, { method: "POST" });
    const data = await res.json();
    setLikeCount(data.likes);
    setLiked(true);
    if (typeof window !== 'undefined') localStorage.setItem(`blog-liked-${slug}`, '1');
  }

  if (!post) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary_green mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading article...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back button */}
        <Link href="/blog" className="inline-flex items-center gap-2 text-primary_green font-semibold hover:underline mb-8 group">
          <span className="text-xl group-hover:-translate-x-1 transition-transform">←</span>
          <span>Back to Blog</span>
        </Link>
        
        {/* Article container */}
        <article className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Cover image */}
          {post.image && (
            <div className="w-full h-96 bg-gray-100 overflow-hidden">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          {/* Content */}
          <div className="p-8 md:p-12">
            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {post.tags.map((tag: string) => (
                  <span key={tag} className="bg-primary_green/10 text-primary_green text-sm font-semibold px-4 py-1.5 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            
            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6 text-gray-900 leading-tight">
              {post.title}
            </h1>
            
            {/* Meta info */}
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-8 pb-8 border-b">
              <span>{new Date(post.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <span>•</span>
              <span>{post.readTime || '5 min read'}</span>
              <span>•</span>
              <span>{likeCount} likes</span>
            </div>
            
            {/* Article content, rendered as markdown so ** bold **, headers,
                links, and lists actually show up styled. GFM enables tables +
                strikethrough. */}
            <div className="blog-body max-w-none text-gray-700 leading-relaxed mb-12">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: (p) => <h1 className="text-3xl font-bold text-gray-900 mt-10 mb-4" {...p} />,
                  h2: (p) => <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-3" {...p} />,
                  h3: (p) => <h3 className="text-xl font-bold text-gray-900 mt-6 mb-2" {...p} />,
                  p:  (p) => <p className="text-base leading-relaxed my-4" {...p} />,
                  a:  (p) => <a className="text-primary_green underline underline-offset-2 hover:text-darkest_green" target={p.href?.startsWith("http") ? "_blank" : undefined} rel={p.href?.startsWith("http") ? "noopener noreferrer" : undefined} {...p} />,
                  ul: (p) => <ul className="list-disc pl-6 my-4 space-y-1" {...p} />,
                  ol: (p) => <ol className="list-decimal pl-6 my-4 space-y-1" {...p} />,
                  li: (p) => <li className="leading-relaxed" {...p} />,
                  blockquote: (p) => <blockquote className="border-l-4 border-primary_green pl-4 italic text-gray-600 my-6" {...p} />,
                  code: (p) => <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" {...p} />,
                  img: (p) => <img className="rounded-lg my-6 max-w-full h-auto" {...p} />,
                  hr: () => <hr className="my-8 border-gray-200" />,
                }}
              >
                {post.content || ""}
              </ReactMarkdown>
            </div>
            
            {/* Like button */}
            <div className="flex items-center justify-between pt-8 border-t">
              <button
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold shadow-md transition-all duration-200 ${
                  liked
                    ? 'bg-red-50 text-red-600 border-2 border-red-200 cursor-not-allowed'
                    : 'bg-primary_green text-white hover:bg-primary_green/90 hover:scale-105 active:scale-95'
                }`}
                onClick={handleLike}
                disabled={liked}
                aria-label="Like this post"
              >
                <span>{liked ? 'Liked!' : 'Like this article'}</span>
                <span className="font-bold">({likeCount})</span>
              </button>
              
              <Link
                href="/blog"
                className="text-primary_green font-semibold hover:underline"
              >
                Read more articles →
              </Link>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
}
